/**
 * Model texts (21-10-2026, Jordy, fase 1): per model en platform de welkomst-DM
 * voor nieuwe volgers en de profiel-bio. Opslaan = alleen in onze DB; er gaat
 * NIETS naar FC of F2F (publiceren is handmatig kopiëren). Alleen owner/admin.
 *
 * - Welkomst: zolang niemand hem bevestigt, de tekst die het platform zelf
 *   stuurt (afgeleid uit onze berichten), gemarkeerd "derived – check".
 * - Bewerken zoals Fan/Model info (Veld: meteen typen, opslaan bij verlaten).
 * - AI per veld (Generate / Improve) en in bulk met review vóór opslaan.
 * - Stad en land zijn INTERN: de server geeft ze de AI niet mee en keurt een
 *   tekst die ze noemt af.
 */
import { useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import { useApi } from '../lib/useApi'
import { ErrorNote, Loading, Panel, Tag } from '../components/ui'
import { ModelNaam } from '../components/ModelNaam'
import { Veld } from '../components/InlineVeld'
import { dtTx } from '../lib/format'

type Soort = 'welkom' | 'bio'
interface VeldData {
  text: string
  source: 'afgeleid' | 'bevestigd' | 'ai' | null
  saved: boolean
  updated_at?: string | null
  updated_by?: string | null
  derived?: { conversations: number; last_seen: string; with_media: boolean }
}
interface ModelRij {
  persona_id: number
  slug: string
  stage_name: string
  platform: 'fc' | 'f2f'
  language: 'en' | 'nl'
  hidden: boolean
  fields: Record<Soort, VeldData>
  persona_missing: string[]
}
interface Voorstel { text: string | null; issues: string[]; error?: string | null; rejected_text?: string | null }
interface BulkResultaat {
  persona_id: number; slug: string; stage_name: string; platform: string; field: 'welcome' | 'bio'
  current: string; proposal: string | null; issues: string[]
}
interface BulkStand {
  running?: boolean; interrupted?: boolean; mode?: string; total?: number; done?: number; current?: string | null
  results?: BulkResultaat[]; finished_at?: string | null; error?: string | null
}

const VELD_NAAM: Record<Soort, string> = { welkom: 'Welcome DM', bio: 'Bio' }
const VELD_API: Record<Soort, 'welcome' | 'bio'> = { welkom: 'welcome', bio: 'bio' }
const ISSUE_LABEL: Record<string, string> = {
  too_short: 'too short', too_long: 'too long', link_or_handle: 'link or @handle', number: 'number',
  currency: 'currency', location: 'city/country (internal!)', location_or_meeting: 'location or meeting',
  price_words: 'price words', daily_limit: 'daily AI limit reached', ai_error: 'AI error', empty: 'empty answer',
}
const issueTekst = (i: string) => ISSUE_LABEL[i] ?? i.replace(/_/g, ' ')

async function kopieer(tekst: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(tekst)
    return true
  } catch {
    try {
      const t = document.createElement('textarea')
      t.value = tekst
      document.body.appendChild(t)
      t.select()
      const ok = document.execCommand('copy')
      document.body.removeChild(t)
      return ok
    } catch { return false }
  }
}

function BronTag({ v, soort }: { v: VeldData; soort: Soort }) {
  if (v.source === 'afgeleid') return <Tag kind="warn" title="Taken from the welcome the platform sends now. Check it, then confirm.">derived – check</Tag>
  if (v.source === 'bevestigd') return <Tag kind="ok">confirmed</Tag>
  if (v.source === 'ai') return <Tag kind="acc">AI</Tag>
  return soort === 'welkom'
    ? <Tag kind="bad" title="No welcome found in the last 60 days: the setting is off, rare, or very short.">no welcome found</Tag>
    : <Tag kind="mut">empty</Tag>
}

function TekstVeld({ m, soort, onSaved }: { m: ModelRij; soort: Soort; onSaved: () => void }) {
  const v = m.fields[soort]
  const [bezig, setBezig] = useState<'' | 'genereer' | 'verbeter'>('')
  const [voorstel, setVoorstel] = useState<Voorstel | null>(null)
  const [gekopieerd, setGekopieerd] = useState(false)
  const pad = `/api/admin/model-texts/${m.persona_id}/${VELD_API[soort]}`
  const sla = async (tekst: string, bron: 'bevestigd' | 'ai') => {
    await api.post(pad, { text: tekst, source: bron })
    onSaved()
  }
  const vraagAi = async (modus: 'genereer' | 'verbeter') => {
    setBezig(modus); setVoorstel(null)
    try {
      setVoorstel(await api.post<Voorstel>(`${pad}/ai`, { mode: modus, current: v.text }))
    } catch (e) {
      setVoorstel({ text: null, issues: [], error: String((e as Error)?.message || e) })
    } finally { setBezig('') }
  }
  const testid = `mt-${m.slug}-${soort}`
  return (
    <div data-testid={testid} style={{ display: 'grid', gap: 4 }}>
      <Veld label={VELD_NAAM[soort]} waarde={v.text} meerregelig testid={`${testid}-veld`}
            opslaan={(w) => sla(w, 'bevestigd')}
            extra={<BronTag v={v} soort={soort} />} />
      <div className="row" style={{ gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <button className="btn ghost sm" type="button" disabled={!!bezig} data-testid={`${testid}-genereer`}
                onClick={() => { void vraagAi('genereer') }}>{bezig === 'genereer' ? 'Writing…' : 'Generate'}</button>
        <button className="btn ghost sm" type="button" disabled={!!bezig || !v.text.trim()} data-testid={`${testid}-verbeter`}
                onClick={() => { void vraagAi('verbeter') }}>{bezig === 'verbeter' ? 'Writing…' : 'Improve'}</button>
        <button className="btn ghost sm" type="button" disabled={!v.text.trim()} data-testid={`${testid}-kopieer`}
                onClick={() => { void kopieer(v.text).then((ok) => { setGekopieerd(ok); setTimeout(() => setGekopieerd(false), 1500) }) }}>
          {gekopieerd ? 'Copied' : 'Copy'}</button>
        {v.source === 'afgeleid' ? (
          <button className="btn ghost sm" type="button" data-testid={`${testid}-bevestig`}
                  onClick={() => { void sla(v.text, 'bevestigd') }}>Confirm</button>
        ) : null}
        <span className="mut2" style={{ fontSize: 11 }}>
          {v.derived ? `sent to ${v.derived.conversations} new followers · last ${v.derived.last_seen}${v.derived.with_media ? ' · with attachment' : ''}`
            : v.saved && v.updated_at ? `saved ${dtTx(v.updated_at)}${v.updated_by ? ` by ${v.updated_by}` : ''}` : ''}
        </span>
      </div>
      {voorstel ? (
        <div className="mtvoorstel" data-testid={`${testid}-voorstel`}>
          {voorstel.text ? (
            <>
              <div style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{voorstel.text}</div>
              <div className="row" style={{ gap: 6, marginTop: 6 }}>
                <button className="btn primary sm" type="button" data-testid={`${testid}-gebruik`}
                        onClick={() => { const t = voorstel.text ?? ''; setVoorstel(null); void sla(t, 'ai') }}>Use</button>
                <button className="btn ghost sm" type="button" onClick={() => setVoorstel(null)}>Discard</button>
              </div>
            </>
          ) : (
            <div className="mut2" style={{ fontSize: 12 }}>
              {voorstel.error || 'No proposal.'}{voorstel.issues?.length ? ` (${voorstel.issues.map(issueTekst).join(', ')})` : ''}
              <button className="linkbtn" type="button" onClick={() => setVoorstel(null)}>Close</button>
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}

function ModelKaart({ groep, onSaved }: { groep: ModelRij[]; onSaved: () => void }) {
  const [gekopieerd, setGekopieerd] = useState(false)
  const alles = groep.map((m) => [
    `${m.stage_name} · ${m.platform.toUpperCase()}`,
    `Welcome DM:\n${m.fields.welkom.text || '—'}`,
    `Bio:\n${m.fields.bio.text || '—'}`,
  ].join('\n')).join('\n\n')
  return (
    <div className="card mtkaart" data-testid="mt-model">
      <div className="row" style={{ alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <ModelNaam slug={groep[0].slug} naam={groep[0].stage_name} size={22} />
        {groep.some((m) => m.hidden) ? <Tag kind="mut">hidden</Tag> : null}
        <span style={{ flex: 1 }} />
        <button className="btn ghost sm" type="button" data-testid="mt-kopieer-alles"
                onClick={() => { void kopieer(alles).then((ok) => { setGekopieerd(ok); setTimeout(() => setGekopieerd(false), 1500) }) }}>
          {gekopieerd ? 'Copied' : 'Copy all'}</button>
      </div>
      <div className="mtplatforms">
        {groep.map((m) => (
          <section key={m.persona_id} className="mtplatform" data-testid={`mt-platform-${m.slug}`}>
            <div className="row" style={{ gap: 6, alignItems: 'center', marginBottom: 4 }}>
              <Tag kind={m.platform === 'fc' ? 'fc' : 'f2f'}>{m.platform === 'fc' ? 'FC' : 'F2F'}</Tag>
              <span className="mut2" style={{ fontSize: 11 }}>
                {m.language === 'en' ? 'English' : 'Dutch'} · <span className="mono">{m.slug}</span>
              </span>
            </div>
            {m.persona_missing.length ? (
              <div className="mut2" style={{ fontSize: 11, marginBottom: 4 }}>
                Persona info missing for the AI: {m.persona_missing.join(', ')}
              </div>
            ) : null}
            <div style={{ display: 'grid', gap: 10 }}>
              <TekstVeld m={m} soort="welkom" onSaved={onSaved} />
              <TekstVeld m={m} soort="bio" onSaved={onSaved} />
            </div>
          </section>
        ))}
      </div>
    </div>
  )
}

function Bulk({ onSaved }: { onSaved: () => void }) {
  const [stand, setStand] = useState<BulkStand | null>(null)
  const [modus, setModus] = useState<'genereer' | 'verbeter'>('genereer')
  const [velden, setVelden] = useState<Record<'welcome' | 'bio', boolean>>({ welcome: true, bio: true })
  const [verborgen, setVerborgen] = useState(false)
  const [gekozen, setGekozen] = useState<Record<string, boolean>>({})
  const [fout, setFout] = useState('')
  const [melding, setMelding] = useState('')
  const laad = async () => {
    try { setStand(await api.get<BulkStand>('/api/admin/model-texts/bulk')) } catch { /* stil */ }
  }
  useEffect(() => { void laad() }, [])
  useEffect(() => {
    if (!stand?.running) return
    const t = setTimeout(() => { void laad() }, 2000)
    return () => clearTimeout(t)
  }, [stand])
  const sleutel = (r: BulkResultaat) => `${r.persona_id}:${r.field}`
  const klaar = (stand?.results ?? []).filter((r) => r.proposal)
  useEffect(() => {
    if (stand && !stand.running) setGekozen(Object.fromEntries(klaar.map((r) => [sleutel(r), true])))
  }, [stand?.running, stand?.finished_at]) // eslint-disable-line react-hooks/exhaustive-deps
  const start = async () => {
    setFout(''); setMelding('')
    try {
      setStand(await api.post<BulkStand>('/api/admin/model-texts/bulk', {
        mode: modus, fields: (['welcome', 'bio'] as const).filter((f) => velden[f]), include_hidden: verborgen,
      }))
    } catch (e) { setFout(String((e as Error)?.message || e)) }
  }
  const opslaan = async () => {
    const items = klaar.filter((r) => gekozen[sleutel(r)]).map((r) => ({ persona_id: r.persona_id, field: r.field, text: r.proposal }))
    try {
      const uit = await api.post<{ saved: unknown[]; skipped: unknown[] }>('/api/admin/model-texts/bulk/save', { items })
      setMelding(`${uit.saved.length} saved${uit.skipped.length ? `, ${uit.skipped.length} skipped (failed the check)` : ''}.`)
      onSaved()
    } catch (e) { setFout(String((e as Error)?.message || e)) }
  }
  const loopt = !!stand?.running
  const n = Object.values(gekozen).filter(Boolean).length
  return (
    <Panel title="Generate / improve all" inklap="mt-bulk"
           desc="The AI writes proposals in the background; nothing is saved until you review them and press Save selected.">
      <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'center' }} data-testid="mt-bulk">
        <select className="inp" style={{ width: 'auto' }} value={modus} aria-label="Mode"
                onChange={(e) => setModus(e.target.value as 'genereer' | 'verbeter')}>
          <option value="genereer">Generate empty fields</option>
          <option value="verbeter">Improve filled fields</option>
        </select>
        {(['welcome', 'bio'] as const).map((f) => (
          <label key={f} className="row" style={{ gap: 4, fontSize: 13 }}>
            <input type="checkbox" checked={velden[f]} onChange={(e) => setVelden({ ...velden, [f]: e.target.checked })} />
            {f === 'welcome' ? 'Welcome DM' : 'Bio'}
          </label>
        ))}
        <label className="row" style={{ gap: 4, fontSize: 13 }}>
          <input type="checkbox" checked={verborgen} onChange={(e) => setVerborgen(e.target.checked)} /> include hidden models
        </label>
        <button className="btn primary sm" type="button" disabled={loopt || !(velden.welcome || velden.bio)}
                data-testid="mt-bulk-start" onClick={() => { void start() }}>Start</button>
      </div>
      {fout ? <div className="note" style={{ marginTop: 8 }}>{fout}</div> : null}
      {loopt ? (
        <div style={{ marginTop: 10 }} data-testid="mt-bulk-voortgang">
          <div className="mut2" style={{ fontSize: 12, marginBottom: 4 }}>
            Writing… {stand?.done ?? 0} / {stand?.total ?? 0}{stand?.current ? ` · now: ${stand.current}` : ''}
          </div>
          <div style={{ height: 6, background: 'var(--line)', borderRadius: 3 }}>
            <div style={{ height: 6, width: `${stand?.total ? Math.round(100 * (stand.done ?? 0) / stand.total) : 0}%`,
                          background: 'var(--acc)', borderRadius: 3 }} />
          </div>
        </div>
      ) : null}
      {!loopt && stand?.error ? <div className="note" style={{ marginTop: 8 }}>{stand.error}</div> : null}
      {!loopt && stand?.interrupted ? <div className="note" style={{ marginTop: 8 }}>The last run was interrupted (for example by an update); the proposals so far are below.</div> : null}
      {!loopt && (stand?.results ?? []).length ? (
        <div style={{ marginTop: 10, display: 'grid', gap: 8 }} data-testid="mt-bulk-review">
          <div className="row" style={{ gap: 8, alignItems: 'center' }}>
            <b style={{ fontSize: 13 }}>Review</b>
            <span className="mut2" style={{ fontSize: 12 }}>
              {klaar.length} proposal(s){stand?.finished_at ? ` · ${dtTx(stand.finished_at)}` : ''}
            </span>
            <span style={{ flex: 1 }} />
            <button className="btn primary sm" type="button" disabled={!n} data-testid="mt-bulk-opslaan"
                    onClick={() => { void opslaan() }}>Save selected ({n})</button>
          </div>
          {melding ? <div className="mut2" style={{ fontSize: 12 }}>{melding}</div> : null}
          {(stand?.results ?? []).map((r) => (
            <div key={sleutel(r)} className="mtreview" data-testid="mt-bulk-rij">
              <label className="row" style={{ gap: 6, alignItems: 'flex-start' }}>
                <input type="checkbox" disabled={!r.proposal} checked={!!gekozen[sleutel(r)]}
                       onChange={(e) => setGekozen({ ...gekozen, [sleutel(r)]: e.target.checked })} />
                <span style={{ fontSize: 12.5 }}>
                  <b>{r.stage_name}</b> <span className="mut2">· {r.platform.toUpperCase()} · {r.field === 'welcome' ? 'Welcome DM' : 'Bio'}</span>
                </span>
              </label>
              {r.proposal ? <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, marginLeft: 22 }}>{r.proposal}</div>
                : <div className="mut2" style={{ fontSize: 12, marginLeft: 22 }}>No proposal ({r.issues.map(issueTekst).join(', ') || 'unknown'})</div>}
            </div>
          ))}
        </div>
      ) : null}
    </Panel>
  )
}

export default function ModelTeksten() {
  const q = useApi<{ models: ModelRij[] }>('/api/admin/model-texts', [])
  const [zoek, setZoek] = useState('')
  const [platform, setPlatform] = useState<'all' | 'fc' | 'f2f'>('all')
  const groepen = useMemo(() => {
    const per = new Map<string, ModelRij[]>()
    for (const m of q.data?.models ?? []) {
      if (platform !== 'all' && m.platform !== platform) continue
      if (zoek.trim() && !`${m.stage_name} ${m.slug}`.toLowerCase().includes(zoek.trim().toLowerCase())) continue
      const k = m.stage_name.trim().toLowerCase()
      per.set(k, [...(per.get(k) ?? []), m])
    }
    return [...per.values()].map((g) => g.sort((a, b) => a.platform.localeCompare(b.platform)))
  }, [q.data, zoek, platform])
  const modellen = q.data?.models ?? []
  const zonderWelkom = modellen.filter((m) => !m.fields.welkom.text.trim()).length
  return (
    <div className="page" data-testid="model-teksten">
      <div className="note" style={{ marginBottom: 12 }}>
        Texts are only saved here. Nothing is sent to FanCentro or F2F: copy a text and paste it on the platform yourself.
        City and country are internal and never go into a welcome or bio.
      </div>
      <Bulk onSaved={q.refetch} />
      <Panel title="Models" desc={q.data ? `${modellen.length} model accounts · ${zonderWelkom} without a welcome text` : undefined}
             right={(
               <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                 <input className="inp" style={{ width: 180 }} placeholder="Search model" value={zoek}
                        aria-label="Search model" onChange={(e) => setZoek(e.target.value)} />
                 <select className="inp" style={{ width: 'auto' }} value={platform} aria-label="Platform"
                         onChange={(e) => setPlatform(e.target.value as 'all' | 'fc' | 'f2f')}>
                   <option value="all">All platforms</option><option value="fc">FC</option><option value="f2f">F2F</option>
                 </select>
               </div>
             )}>
        {q.loading && !q.data ? <Loading /> : null}
        {q.error ? <ErrorNote error={q.error} /> : null}
        <div style={{ display: 'grid', gap: 12 }}>
          {groepen.map((g) => <ModelKaart key={g[0].persona_id} groep={g} onSaved={q.refetch} />)}
        </div>
      </Panel>
    </div>
  )
}
